import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanyGeneralSettings.css";
import {
  useCompanyBranding,
} from "../../CompanyBrandingContext";

const API_URL = "/api";

const emptyForm = {
  business_name: "",
  commercial_name: "",
  nit: "",
  document_type: "",
  email: "",
  phone: "",
  mobile: "",
  website: "",
  address: "",
  city: "",
  state: "",
  country: "Colombia",
  postal_code: "",
  currency: "COP",
  timezone: "America/Bogota",
  language: "Español",
  logo: "",
  favicon: "",
  primary_color: "#1f5d7a",
  secondary_color: "#16465e",
  login_background_color: "#eef2ff",
  is_active: true,
};

const toText = (value) => value ?? "";

const normalizeSettings = (data = {}) => ({
  ...emptyForm,
  ...data,

  business_name: toText(data.business_name),
  commercial_name: toText(data.commercial_name),
  nit: toText(data.nit),
  document_type: toText(data.document_type),

  email: toText(data.email),
  phone: toText(data.phone),
  mobile: toText(data.mobile),
  website: toText(data.website),

  address: toText(data.address),
  city: toText(data.city),
  state: toText(data.state),
  country: toText(data.country) || "Colombia",
  postal_code: toText(data.postal_code),

  currency: toText(data.currency) || "COP",
  timezone: toText(data.timezone) || "America/Bogota",
  language: toText(data.language) || "Español",

  logo: toText(data.logo),
  favicon: toText(data.favicon),
  primary_color:
    toText(data.primary_color) || "#1f5d7a",
  secondary_color:
    toText(data.secondary_color) || "#16465e",
  login_background_color:
    toText(data.login_background_color) || "#eef2ff",
  is_active: data.is_active ?? true,
});

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

export default function CompanyGeneralSettings({
  readOnly = false,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploadingImage, setUploadingImage] = useState("");
  const { applyBranding } = useCompanyBranding();

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/settings/company/me`,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeSettings(response.data));
      applyBranding(response.data);
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
        "No fue posible cargar la configuración de la empresa."
      );
    } finally {
      setLoading(false);
    }
    }, [applyBranding]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const getPublicImageUrl = (path) => {
    if (!path) {
      return "";
    }

    return path.startsWith("http")
      ? path
      : `${API_URL}${path}`;
  };

  const handleBrandingUpload = async (
    event,
    endpoint,
    field,
    label
  ) => {
    const file = event.target.files?.[0];

    if (!file || readOnly) {
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploadingImage(field);

      const response = await axios.post(
        `${API_URL}/settings/company/me/${endpoint}`,
        formData,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeSettings(response.data));
      applyBranding(response.data);

      toast.success(`${label} actualizado correctamente.`);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        `No fue posible cargar el ${label.toLowerCase()}.`
      );
    } finally {
      setUploadingImage("");
      event.target.value = "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (readOnly) {
      return;
    }

    if (!toText(form.business_name).trim()) {
      toast.warning("La razón social es obligatoria.");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        business_name: toText(form.business_name).trim(),
        commercial_name: toText(form.commercial_name).trim() || null,

        nit: toText(form.nit).trim() || null,
        document_type: toText(form.document_type).trim() || null,

        email: toText(form.email).trim() || null,
        phone: toText(form.phone).trim() || null,
        mobile: toText(form.mobile).trim() || null,
        website: toText(form.website).trim() || null,

        address: toText(form.address).trim() || null,
        city: toText(form.city).trim() || null,
        state: toText(form.state).trim() || null,
        country: toText(form.country).trim() || "Colombia",
        postal_code: toText(form.postal_code).trim() || null,

        currency: toText(form.currency) || "COP",
        timezone: toText(form.timezone) || "America/Bogota",
        language: toText(form.language) || "Español",

        logo: toText(form.logo).trim() || null,
        favicon: toText(form.favicon).trim() || null,
        primary_color: toText(form.primary_color).trim() || "#1f5d7a",
        secondary_color: toText(form.secondary_color).trim() || "#16465e",
        login_background_color: toText(form.login_background_color).trim() || "#eef2ff",
        is_active: form.is_active ?? true,
      };

      const response = await axios.put(
        `${API_URL}/settings/company/me`,
        payload,
        {
          headers: getHeaders(),
        }
      );

      setForm(normalizeSettings(response.data));
      applyBranding(response.data);

      toast.success("Configuración de empresa actualizada.");
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
        "No fue posible guardar la configuración."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        Cargando configuración...
      </div>
    );
  }

  return (
    <form
      className="company-settings-card"
      onSubmit={handleSubmit}
    >
      <div className="company-settings-header">
        <h3>Configuración Empresa</h3>
        <p>
          Información general visible en documentos y
          reportes.
        </p>
      </div>

      <div className="company-settings-body">
        <fieldset
          className="company-settings-fieldset"
          disabled={readOnly}
        >
          <div className="settings-form-grid">
            <div className="floating-field">
              <input
                id="business_name"
                name="business_name"
                value={form.business_name}
                onChange={handleChange}
                placeholder=" "
                required
              />
              <label htmlFor="business_name">
                Razón social
              </label>
            </div>

            <div className="floating-field">
              <input
                id="commercial_name"
                name="commercial_name"
                value={form.commercial_name}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="commercial_name">
                Nombre comercial
              </label>
            </div>

            <div className="floating-field">
              <input
                id="nit"
                name="nit"
                value={form.nit}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="nit">NIT</label>
            </div>

            <div className="floating-field">
              <input
                id="document_type"
                name="document_type"
                value={form.document_type}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="document_type">
                Tipo de documento
              </label>
            </div>

            <div className="floating-field">
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="email">
                Correo electrónico
              </label>
            </div>

            <div className="floating-field">
              <input
                id="phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="phone">Teléfono</label>
            </div>

            <div className="floating-field">
              <input
                id="mobile"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="mobile">Celular</label>
            </div>

            <div className="floating-field">
              <input
                id="website"
                name="website"
                value={form.website}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="website">Sitio web</label>
            </div>

            <div className="floating-field settings-field-full">
              <input
                id="address"
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="address">Dirección</label>
            </div>

            <div className="floating-field">
              <input
                id="city"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="city">Ciudad</label>
            </div>

            <div className="floating-field">
              <input
                id="state"
                name="state"
                value={form.state}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="state">Departamento</label>
            </div>

            <div className="floating-field">
              <input
                id="country"
                name="country"
                value={form.country}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="country">País</label>
            </div>

            <div className="floating-field">
              <input
                id="postal_code"
                name="postal_code"
                value={form.postal_code}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="postal_code">
                Código postal
              </label>
            </div>

            <div className="floating-field">
              <select
                id="currency"
                name="currency"
                value={form.currency}
                onChange={handleChange}
              >
                <option value="COP">
                  COP - Peso colombiano
                </option>
                <option value="USD">
                  USD - Dólar estadounidense
                </option>
                <option value="EUR">
                  EUR - Euro
                </option>
              </select>
              <label htmlFor="currency">Moneda</label>
            </div>

            <div className="floating-field">
              <select
                id="language"
                name="language"
                value={form.language}
                onChange={handleChange}
              >
                <option value="Español">Español</option>
                <option value="English">English</option>
              </select>
              <label htmlFor="language">Idioma</label>
            </div>

            <div className="floating-field">
              <input
                id="timezone"
                name="timezone"
                value={form.timezone}
                onChange={handleChange}
                placeholder=" "
              />
              <label htmlFor="timezone">
                Zona horaria
              </label>
            </div>
          </div>
          <section className="company-branding-section">
            <div>
              <h4>Identidad visual</h4>
              <p>
                Personaliza el logo, ícono y colores del
                sistema de esta empresa.
              </p>
            </div>

            <div className="company-branding-grid">
              <label className="branding-upload-field">
                <span>Logo de la empresa</span>

                {form.logo ? (
                  <img
                    className="branding-logo-preview"
                    src={getPublicImageUrl(form.logo)}
                    alt="Logo de la empresa"
                  />
                ) : (
                  <div className="branding-image-placeholder">
                    Sin logo
                  </div>
                )}

                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={(event) =>
                    handleBrandingUpload(
                      event,
                      "logo",
                      "logo",
                      "Logo"
                    )
                  }
                />

                <small>
                  {uploadingImage === "logo"
                    ? "Cargando logo..."
                    : "PNG, JPG o WEBP. Máximo 2 MB."}
                </small>
              </label>

              <label className="branding-upload-field">
                <span>Ícono del navegador</span>

                {form.favicon ? (
                  <img
                    className="branding-favicon-preview"
                    src={getPublicImageUrl(form.favicon)}
                    alt="Ícono de la empresa"
                  />
                ) : (
                  <div className="branding-image-placeholder">
                    Sin ícono
                  </div>
                )}

                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.ico"
                  onChange={(event) =>
                    handleBrandingUpload(
                      event,
                      "favicon",
                      "favicon",
                      "Ícono"
                    )
                  }
                />

                <small>
                  {uploadingImage === "favicon"
                    ? "Cargando ícono..."
                    : "PNG, JPG, WEBP o ICO. Máximo 2 MB."}
                </small>
              </label>

              <label className="branding-color-field">
                <span>Color principal</span>
                <input
                  type="color"
                  name="primary_color"
                  value={form.primary_color}
                  onChange={handleChange}
                />
                <code>{form.primary_color}</code>
              </label>

              <label className="branding-color-field">
                <span>Color secundario</span>
                <input
                  type="color"
                  name="secondary_color"
                  value={form.secondary_color}
                  onChange={handleChange}
                />
                <code>{form.secondary_color}</code>
              </label>

              <label className="branding-color-field">
                <span>Fondo de inicio de sesión</span>
                <input
                  type="color"
                  name="login_background_color"
                  value={form.login_background_color}
                  onChange={handleChange}
                />
                <code>{form.login_background_color}</code>
              </label>
            </div>
          </section>
        </fieldset>

        <div className="company-settings-actions">
          {!readOnly ? (
            <button type="submit" disabled={saving}>
              {saving
                ? "Guardando..."
                : "Guardar configuración"}
            </button>
          ) : (
            <span className="settings-view-only">
              Solo consulta
            </span>
          )}
        </div>
      </div>
    </form>
  );
}